import { useEffect, useState } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DocumentUploader } from "./DocumentUploader";
import { AttachmentGallery } from "./AttachmentGallery";
import { toast } from "sonner";
import { Handshake, FileText, Plus, X, Trash2, Send, Loader2, ChevronDown, CheckCircle2, Download } from "lucide-react";
import { CrediusModelliEditor } from "./CrediusModelliEditor";

const SEL = "h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1";
// Iter111 · label "Affiliato" (valore DB, invariato) → "Convenzionato" (visualizzato)
const STATO_LABEL = { Affiliato: "Convenzionato" };
const statoLabel = (s) => STATO_LABEL[s] || s;
const STATO_STYLE = {
  Proposta: "bg-slate-100 text-slate-700 border-slate-300",
  "Documenti inviati": "bg-amber-100 text-amber-800 border-amber-300",
  "Documenti ricevuti": "bg-sky-100 text-sky-800 border-sky-300",  // legacy: pratiche pre-Iter122k71
  Affiliato: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Sospesa: "bg-yellow-100 text-yellow-800 border-yellow-300",  // Iter122k71
  Cessata: "bg-red-100 text-red-700 border-red-300",
  Revocata: "bg-red-100 text-red-700 border-red-300",  // Iter122k89
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function AffiliazioneCard({ a, meta, onReload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [fees, setFees] = useState(a.fee_percentages || {
    stragiudiziale_interna: 0, stragiudiziale_esterna: 0,
    giudiziale_interna: 0, giudiziale_esterna: 0, sinistro: 0, altro: 0,
  });
  // Iter122k53 · Scheda dati professionista (editor + magic link + PDF)
  const [schedaOpen, setSchedaOpen] = useState(false);
  const [scheda, setScheda] = useState(null);
  const [schedaMeta, setSchedaMeta] = useState({});
  const loadScheda = async () => {
    try {
      const r = await api.get(`/credius/affiliazioni/${a.id}/scheda`);
      setScheda(r.data.scheda || {});
      setSchedaMeta({ magic_link_active: r.data.magic_link_active, magic_link_expires_at: r.data.magic_link_expires_at });
    } catch (err) { toast.error("Errore caricamento scheda"); }
  };
  const toggleScheda = () => {
    if (!schedaOpen && scheda === null) loadScheda();
    setSchedaOpen((s) => !s);
  };
  const setSchedaField = (k, v) => setScheda((s) => ({ ...(s || {}), [k]: v }));
  const salvaScheda = async () => {
    if (!scheda) return;
    await run(() => api.put(`/credius/affiliazioni/${a.id}/scheda`, scheda).then((r) => setScheda(r.data.scheda_professionista || scheda)),
              "Scheda salvata");
    loadScheda();
  };
  const inviaMagicLink = async () => {
    try {
      setBusy(true);
      const { data: res } = await api.post(`/credius/affiliazioni/${a.id}/scheda/magic-link`, { invia_email: true, scadenza_giorni: 7 });
      toast.success("Link scheda inviato al professionista (7 giorni)");
      try { await navigator.clipboard.writeText(res.link); toast.info("Link copiato negli appunti"); } catch {}
      loadScheda();
      onReload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore invio link"); }
    finally { setBusy(false); }
  };
  const scaricaPdfConvenzione = () => openSecureFile(`/credius/affiliazioni/${a.id}/convenzione/pdf`);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onReload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  const salvaFees = async () => {
    // Iter104 fix · whitelist esplicita dei 6 campi numerici (note_fees è stringa optional)
    const FEE_KEYS = ["stragiudiziale_interna", "stragiudiziale_esterna", "giudiziale_interna",
                       "giudiziale_esterna", "sinistro", "altro"];
    const payload = {};
    FEE_KEYS.forEach((k) => { payload[k] = parseFloat(fees[k]) || 0; });
    if (fees.note_fees) payload.note_fees = String(fees.note_fees);
    await run(() => api.post(`/credius/affiliazioni/${a.id}/fees`, payload), "Fee percentages aggiornate");
  };
  const FEE_LABELS = {
    stragiudiziale_interna: "Stragiudiziale · Interna",
    stragiudiziale_esterna: "Stragiudiziale · Esterna",
    giudiziale_interna: "Giudiziale · Interna",
    giudiziale_esterna: "Giudiziale · Esterna",
    sinistro: "Sinistri",
    altro: "Altro",
  };
  const schedaCompletata = !!(a.scheda_professionista && a.scheda_professionista.completata);
  const firmata = !!(a.firma && a.firma.signed_at);
  return (
    <div data-testid={`affiliazione-${a.id}`} className="bg-white rounded-xl border-2 border-slate-200 p-4">
      <button type="button" className="w-full flex flex-wrap items-center gap-2 text-left" onClick={() => setOpen(!open)}>
        <span className="font-bold text-slate-800">{a.nome}</span>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[a.stato] || STATO_STYLE.Proposta}`}>{statoLabel(a.stato)}</span>
        {firmata && (
          <span data-testid={`affiliazione-firmata-${a.id}`}
            className="text-xs font-bold px-2 py-0.5 rounded-full border bg-emerald-600 text-white border-emerald-700">
            ✅ Convenzione sottoscritta
          </span>
        )}
        {!firmata && schedaCompletata && (
          <span data-testid={`affiliazione-scheda-completata-${a.id}`}
            className="text-xs font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300">
            ✓ Scheda completata
          </span>
        )}
        {a.email && <span className="text-xs text-slate-500">{a.email}</span>}
        <ChevronDown className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          <div className="actions-row flex flex-wrap gap-2">
            <Button size="sm" data-testid={`affiliazione-proponi-${a.id}`} disabled={busy || !a.email}
              onClick={() => run(() => api.post(`/credius/affiliazioni/${a.id}/proponi`), "Proposta inviata con i modelli allegati")}
              className="bg-indigo-900 hover:bg-indigo-950">
              <Send className="w-4 h-4 mr-1.5" /> Invia proposta + modelli
            </Button>
            <Button size="sm" data-testid={`affiliazione-pdf-convenzione-${a.id}`} disabled={busy}
              onClick={scaricaPdfConvenzione}
              variant="outline" className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
              <FileText className="w-4 h-4 mr-1.5" /> Genera PDF Convenzione
            </Button>
            <Button size="sm" data-testid={`affiliazione-magic-link-${a.id}`} disabled={busy || !a.email}
              onClick={inviaMagicLink}
              variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <Send className="w-4 h-4 mr-1.5" /> Invia link scheda al professionista
            </Button>
            <select data-testid={`affiliazione-stato-${a.id}`} value={a.stato} disabled={busy}
              onChange={(e) => run(() => api.post(`/credius/affiliazioni/${a.id}/stato`, { stato: e.target.value }), "Stato aggiornato")}
              className="h-9 border-2 border-slate-300 rounded-lg px-2 bg-white text-sm">
              {(meta.affiliazione_stati || []).map((s) => <option key={s} value={s}>{statoLabel(s)}</option>)}
            </select>
            {/* Iter122k89 · Bug 5 · Pulsante Revoca convenzione (solo se firmata e non già cessata/revocata) */}
            {firmata && !["Cessata", "Revocata"].includes(a.stato) && (
              <Button size="sm" variant="outline" disabled={busy}
                data-testid={`affiliazione-revoca-${a.id}`}
                onClick={() => {
                  const motivo = window.prompt(`Confermi la REVOCA della convenzione con ${a.nome}?\n\nQuesta azione:\n• Cambia lo stato in "Revocata"\n• Notifica lo Studio via email\n• Notifica il professionista via email\n• Nasconde la convenzione dal portale del professionista\n\nMotivo (opzionale):`, "");
                  if (motivo === null) return;
                  run(() => api.post(`/credius/affiliazioni/${a.id}/revoca`, { motivo: motivo || "" }), "Convenzione revocata");
                }}
                className="border-rose-300 text-rose-700 hover:bg-rose-50">
                Revoca convenzione
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => window.confirm(`Eliminare la scheda di convenzione di ${a.nome}?`) &&
                run(() => api.delete(`/credius/affiliazioni/${a.id}`), "Eliminata")}
              className="border-red-300 text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
          </div>

          {/* Iter122k67 · Card "Convenzione sottoscritta" con dettagli firma + download PDF */}
          {firmata && (
            <div data-testid={`affiliazione-firma-card-${a.id}`}
              className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 min-w-0 overflow-hidden">
              <div className="flex items-start gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <span className="font-bold text-emerald-900 text-sm sm:text-base leading-snug break-words">
                  Convenzione sottoscritta il {fmt(a.firma.signed_at)}
                </span>
              </div>
              <div className="text-xs text-slate-700 space-y-0.5 mb-3 break-words">
                <div><b>Firmatario:</b> {a.firma.signer_name} {a.firma.signer_cf && `(CF ${a.firma.signer_cf})`}</div>
                <div><b>Metodo:</b> {a.firma.method || "FEA · OTP email + firma grafica"}</div>
                <div><b>IP client:</b> {a.firma.ip || "—"}</div>
                <div className="break-all"><b>Email OTP:</b> {a.firma.signer_email || "—"}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(a.firma.docs || []).map((d) => (
                  <button key={d.file_id} type="button"
                    data-testid={`affiliazione-firma-download-${d.tipo}-${a.id}`}
                    onClick={() => openSecureFile(`/files/${d.file_id}`)}
                    className="w-full min-w-0 flex items-center gap-2 bg-white border-2 border-emerald-200 rounded-lg px-3 py-2 text-xs sm:text-sm font-bold text-emerald-800 hover:bg-emerald-100">
                    <Download className="w-4 h-4 shrink-0" />
                    <span className="truncate min-w-0 flex-1 text-left">{d.filename}</span>
                  </button>
                ))}
                {a.firma.attestato_file_id && (
                  <button type="button"
                    data-testid={`affiliazione-firma-download-attestato-${a.id}`}
                    onClick={() => openSecureFile(`/files/${a.firma.attestato_file_id}`)}
                    className="w-full min-w-0 flex items-center gap-2 bg-white border-2 border-indigo-200 rounded-lg px-3 py-2 text-xs sm:text-sm font-bold text-indigo-800 hover:bg-indigo-100">
                    <Download className="w-4 h-4 shrink-0" />
                    <span className="truncate min-w-0 flex-1 text-left">Attestato di firma</span>
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Iter122k53 · Editor Scheda dati professionista */}
          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-3">
            <button type="button" onClick={toggleScheda}
              data-testid={`affiliazione-scheda-toggle-${a.id}`}
              className="w-full flex items-center gap-2 text-left">
              <FileText className="w-4 h-4 text-indigo-800" />
              <span className="text-[11px] font-bold uppercase text-indigo-900">Scheda dati professionista</span>
              {schedaCompletata && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">Completata dal professionista</span>
              )}
              {schedaMeta.magic_link_active && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">Link inviato — in attesa</span>
              )}
              <ChevronDown className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${schedaOpen ? "rotate-180" : ""}`} />
            </button>
            {schedaOpen && scheda !== null && (
              <div className="mt-3 space-y-2">
                <div className="grid sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Nome / Cognome / Ragione Sociale</Label>
                    <Input data-testid={`scheda-nome-${a.id}`} value={scheda.nome_cognome_ragione_sociale || ""}
                      onChange={(e) => setSchedaField("nome_cognome_ragione_sociale", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Codice Fiscale</Label>
                    <Input data-testid={`scheda-cf-${a.id}`} value={scheda.codice_fiscale || ""}
                      onChange={(e) => setSchedaField("codice_fiscale", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">P. IVA</Label>
                    <Input data-testid={`scheda-piva-${a.id}`} value={scheda.partita_iva || ""}
                      onChange={(e) => setSchedaField("partita_iva", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Categoria professionale</Label>
                    <select value={scheda.categoria_professionale || ""}
                      onChange={(e) => setSchedaField("categoria_professionale", e.target.value)}
                      className={SEL.replace("h-11", "h-9")}>
                      <option value="">— Seleziona —</option>
                      {["Avvocato", "Perito", "Notaio", "Commercialista", "Consulente del lavoro", "Geometra", "Architetto", "Ingegnere", "Altro"].map((x) => (
                        <option key={x} value={x}>{x}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Ordine di appartenenza</Label>
                    <Input value={scheda.ordine_appartenenza || ""}
                      onChange={(e) => setSchedaField("ordine_appartenenza", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Recapiti telefonici</Label>
                    <Input value={scheda.recapiti_telefonici || ""}
                      onChange={(e) => setSchedaField("recapiti_telefonici", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">Email / PEC</Label>
                    <Input value={scheda.recapiti_email_pec || ""}
                      onChange={(e) => setSchedaField("recapiti_email_pec", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Indirizzo e civico</Label>
                    <Input value={scheda.indirizzo || ""}
                      onChange={(e) => setSchedaField("indirizzo", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">CAP</Label>
                    <Input value={scheda.cap || ""}
                      onChange={(e) => setSchedaField("cap", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Città</Label>
                    <Input value={scheda.citta || ""}
                      onChange={(e) => setSchedaField("citta", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Sito internet</Label>
                    <Input value={scheda.sito_internet || ""}
                      onChange={(e) => setSchedaField("sito_internet", e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Regime fiscale</Label>
                    <select value={scheda.regime_fiscale || ""}
                      onChange={(e) => setSchedaField("regime_fiscale", e.target.value)}
                      className={SEL.replace("h-11", "h-9")}>
                      <option value="">— Seleziona —</option>
                      {["Regime Forfettario", "Contabilità semplificata", "Contabilità ordinaria"].map((x) => (
                        <option key={x} value={x}>{x}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Codice SDI</Label>
                    <Input value={scheda.codice_sdi || ""}
                      onChange={(e) => setSchedaField("codice_sdi", e.target.value)} className="h-9 mt-1" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Sedi operative</Label>
                  <Textarea rows={2} value={scheda.sedi_operative || ""}
                    onChange={(e) => setSchedaField("sedi_operative", e.target.value)} className="mt-1" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={!!scheda.obbligo_fatturazione_elettronica}
                    onChange={(e) => setSchedaField("obbligo_fatturazione_elettronica", e.target.checked)} />
                  Obbligo di fatturazione elettronica
                </label>
                <Button size="sm" data-testid={`scheda-salva-${a.id}`} disabled={busy}
                  onClick={salvaScheda} className="bg-indigo-900 hover:bg-indigo-950">Salva scheda</Button>
                {scheda.completata_at && (
                  <div className="text-[10px] text-emerald-800">
                    Completata dal professionista il {fmt(scheda.completata_at)}{scheda.completata_da ? ` (${scheda.completata_da})` : ""}.
                  </div>
                )}
              </div>
            )}
          </div>
          {!a.email && <div className="text-xs text-amber-700">Nessuna email associata: la proposta via email non può essere inviata.</div>}
          <div className="bg-sky-50 border-2 border-sky-200 rounded-lg p-3">
            <div className="text-[11px] font-bold uppercase text-sky-800 mb-2">Fee % dello Studio per tipologia pratica (C4.1)</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
              {Object.entries(FEE_LABELS).map(([key, label]) => (
                <div key={key}>
                  <Label className="text-[10px] uppercase text-slate-500">{label}</Label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.1" min="0" max="100"
                      data-testid={`affiliazione-fee-${a.id}-${key}`}
                      value={fees[key] ?? 0}
                      onChange={(e) => setFees({ ...fees, [key]: e.target.value })}
                      className="h-9" />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                </div>
              ))}
            </div>
            <Button size="sm" data-testid={`affiliazione-fee-salva-${a.id}`} disabled={busy}
              onClick={salvaFees} className="bg-sky-700 hover:bg-sky-800">
              Salva fee %
            </Button>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Documenti sottoscritti archiviati</div>
            {(a.documenti || []).length > 0 ? (
              <AttachmentGallery docs={a.documenti} testidPrefix={`affiliazione-doc-${a.id}`} accent="#312E81" size={72} />
            ) : <div className="text-sm text-slate-400">Nessun documento archiviato.</div>}
            <div className="mt-2">
              <DocumentUploader docs={docs} onChange={setDocs} testid={`affiliazione-uploader-${a.id}`} />
              {docs.length > 0 && (
                <Button size="sm" data-testid={`affiliazione-doc-salva-${a.id}`} disabled={busy}
                  onClick={() => run(() => api.post(`/credius/affiliazioni/${a.id}/documenti`, { file_ids: docs.map((d) => d.id), tipo: "Documento sottoscritto" })
                    .then(() => setDocs([])), "Documenti archiviati")}
                  className="mt-2 bg-indigo-900 hover:bg-indigo-950">Archivia documenti</Button>
              )}
            </div>
          </div>
          {(a.cronologia || []).length > 0 && (
            <div className="text-xs text-slate-500 space-y-1">
              {[...a.cronologia].reverse().slice(0, 6).map((h, i) => (
                <div key={i}>• {h.action} — {h.by} · {fmt(h.at)}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CrediusAffiliazioni() {
  const [meta, setMeta] = useState({});
  const [modelli, setModelli] = useState([]);
  const [affiliazioni, setAffiliazioni] = useState(null);
  const [showModello, setShowModello] = useState(false);
  const [modForm, setModForm] = useState({ nome: "", descrizione: "", ambito: "affiliazione" });
  const [modDocs, setModDocs] = useState([]);
  const [showNuova, setShowNuova] = useState(false);
  const [affForm, setAffForm] = useState({ fornitore_id: "", nome: "", email: "", telefono: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/credius/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/credius/modelli").then((r) => setModelli(r.data)).catch(() => {});
    api.get("/credius/affiliazioni").then((r) => setAffiliazioni(r.data)).catch(() => setAffiliazioni([]));
  };
  useEffect(load, []);

  const salvaModello = async () => {
    if (!modForm.nome.trim() || modDocs.length === 0) { toast.error("Indica nome e file del modello"); return; }
    setBusy(true);
    try {
      await api.post("/credius/modelli", { ...modForm, file_id: modDocs[0].id });
      toast.success("Modello salvato");
      setShowModello(false); setModForm({ nome: "", descrizione: "", ambito: "affiliazione" }); setModDocs([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const salvaAffiliazione = async () => {
    setBusy(true);
    try {
      await api.post("/credius/affiliazioni", affForm);
      toast.success("Proposta di convenzione creata");
      setShowNuova(false); setAffForm({ fornitore_id: "", nome: "", email: "", telefono: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="credius-affiliazioni" className="space-y-6">
      {/* Iter122k60 · Editor testi dei 3 modelli (Convenzione/Privacy/Consenso) */}
      <CrediusModelliEditor />

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Handshake className="w-5 h-5 text-indigo-800" />
          <h2 className="font-bold text-indigo-950">Avvocati e studi legali — convenzioni</h2>
          <Button size="sm" data-testid="affiliazione-add-btn" onClick={() => setShowNuova(!showNuova)}
            variant="outline" className="ml-auto border-indigo-300 text-indigo-800 hover:bg-indigo-50">
            <Plus className="w-4 h-4 mr-1" /> Proponi convenzione
          </Button>
        </div>
        {showNuova && (
          <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-4 mb-3 space-y-3">
            <div>
              <Label>Fornitore già presente (categoria Avvocato-Studi Legali)</Label>
              <select data-testid="affiliazione-fornitore" value={affForm.fornitore_id}
                onChange={(e) => {
                  const f = (meta.avvocati || []).find((x) => x.id === e.target.value);
                  setAffForm({ ...affForm, fornitore_id: e.target.value, nome: f?.ragione_sociale || affForm.nome, email: f?.email || affForm.email });
                }} className={SEL}>
                <option value="">— Nuovo nominativo (compila sotto) —</option>
                {(meta.avvocati || []).map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nominativo / Studio legale *</Label>
                <Input data-testid="affiliazione-nome" value={affForm.nome} onChange={(e) => setAffForm({ ...affForm, nome: e.target.value })} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="affiliazione-email" value={affForm.email} onChange={(e) => setAffForm({ ...affForm, email: e.target.value })} className="h-11 mt-1" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Telefono</Label>
                <Input value={affForm.telefono} onChange={(e) => setAffForm({ ...affForm, telefono: e.target.value })} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Note</Label>
                <Input value={affForm.note} onChange={(e) => setAffForm({ ...affForm, note: e.target.value })} className="h-11 mt-1" />
              </div>
            </div>
            <div className="actions-row flex flex-wrap gap-3">
              <Button data-testid="affiliazione-salva" disabled={busy || !affForm.nome.trim()} onClick={salvaAffiliazione}
                className="h-11 bg-indigo-900 hover:bg-indigo-950">Crea proposta</Button>
              <Button variant="outline" className="h-11" onClick={() => setShowNuova(false)}>Annulla</Button>
            </div>
          </div>
        )}
        {affiliazioni === null ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-800" /></div>
        ) : affiliazioni.length === 0 ? (
          <div className="text-sm text-slate-400">Nessuna convenzione presente.</div>
        ) : (
          <div className="space-y-2">
            {affiliazioni.map((a) => <AffiliazioneCard key={a.id} a={a} meta={meta} onReload={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}
